import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';

// The contact form of the knowledge base, a standard form modal which creates a support ticket
const KnowledgeBaseContactForm: React.FC<{ viewKey?: string }> = ({ viewKey }) => {
    const { isLoggedIn, firstName, lastName } = useAuthContext();
    const userName = [firstName, lastName].filter(Boolean).join(' ');

    // A logged in user sends as itself, a visitor fills in its name and email
    const requesterFields: FieldType[] = isLoggedIn
        ? [{ name: 'requester', label: 'knowledge_base.contact.requester_label', type: 'text', placeholder: '', defaultValue: userName, disabled: true }]
        : [
            { name: 'name', label: 'knowledge_base.contact.requester_label', type: 'text', placeholder: 'knowledge_base.contact.name_placeholder' },
            { name: 'email', label: 'knowledge_base.contact.email_label', type: 'email', placeholder: 'knowledge_base.contact.email_placeholder' },
        ];

    // Plug in the fields and their properties
    const fields: FieldType[] = [
        ...requesterFields,
        { name: 'subject', label: 'knowledge_base.contact.subject_label', type: 'text', placeholder: 'knowledge_base.contact.subject_placeholder' },
        { name: 'message', label: 'knowledge_base.contact.message_label', type: 'textarea', placeholder: 'knowledge_base.contact.message_placeholder', rows: 6 },
    ];

    return (
        <FormModal
            viewKey={viewKey}
            apiObject='public_knowledge_base_ticket'
            fields={fields}
            primaryButtonLabel='knowledge_base.contact.send_button'
        />
    );
};

// Returns the function which opens the contact form and thanks the sender afterwards
export const useKnowledgeBaseContactForm = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    return () => initializeModal(
        <KnowledgeBaseContactForm />,
        { title: t('knowledge_base.contact.title'), modalSize: 'small', onSuccess: () => setFloatingAlert({ type: 'success', message: 'knowledge_base.contact.success_text' }) }
    );
};

// The block under an article which invites the reader to get in touch
export const KnowledgeBaseContactPrompt: React.FC = () => {
    const { t } = useTranslation();
    const openContactForm = useKnowledgeBaseContactForm();
    return (
        <div className='kb-article-contact'>
            {t('knowledge_base.contact.not_found_question')} <span className='kb-text-link' onClick={openContactForm}>{t('knowledge_base.contact.button')} <FontAwesomeIcon icon={faArrowRight} /></span>
        </div>
    );
};

export default KnowledgeBaseContactForm;
